import type {
  IOwnershipEdge,
  IOwnershipGraph,
  IOwnershipNode,
  OwnershipBand,
} from './beneficialOwnershipGraph';

export const BENEFICIAL_OWNERSHIP_PDF_BANDS: Array<{
  key: OwnershipBand;
  label: string;
}> = [
  { key: 'owners', label: 'Owners & controllers' },
  { key: 'investors', label: 'Investors' },
  { key: 'holdings', label: 'Holdings & subsidiaries' },
  { key: 'people', label: 'Officers & directors' },
  { key: 'other', label: 'Other connections' },
];

export type BeneficialOwnershipPdfTreeRowStatus =
  | 'normal'
  | 'cycle'
  | 'reference'
  | 'depth-limit';

export interface IBeneficialOwnershipPdfTreeRow {
  key: string;
  nodeKey: string;
  parentKey: string;
  node: IOwnershipNode;
  edge: IOwnershipEdge;
  depth: number;
  childCount: number;
  status: BeneficialOwnershipPdfTreeRowStatus;
}

export interface IBeneficialOwnershipPdfTreeGroup {
  band: OwnershipBand;
  label: string;
  rootCount: number;
  rows: IBeneficialOwnershipPdfTreeRow[];
  // Set on every group of a jurisdiction-branched tree: the country's company
  // these bands belong to. `branchStart` marks the branch's first group, where
  // the PDF draws the company's own card.
  branch?: {
    key: string;
    node: IOwnershipNode;
    connectionCount: number;
  };
  branchStart?: boolean;
}

export interface IBeneficialOwnershipPdfTree {
  groups: IBeneficialOwnershipPdfTreeGroup[];
  rowCount: number;
  truncated: boolean;
}

const emptyDictionary = <T>(): { [key: string]: T } =>
  Object.create(null) as { [key: string]: T };

const fallbackNode = (key: string): IOwnershipNode => ({
  name: key,
  country: '',
  type: '',
  kind: 'company',
  flagged: false,
  findings: '',
  sourceUrl: '',
  sourceDate: '',
});

/**
 * Converts the graph used by the interactive Saved Work tree into bounded,
 * page-friendly rows. A node can still appear under every relationship, but
 * its descendants are expanded only once so converging paths cannot multiply
 * the PDF exponentially. Cycles and depth limits are retained as labelled
 * rows rather than silently disappearing.
 */
export const buildBeneficialOwnershipPdfTree = (
  graph: IOwnershipGraph,
  maxDepth = 4,
  maxRows = 250,
): IBeneficialOwnershipPdfTree => {
  let expandedNodes = emptyDictionary<boolean>();
  const groups: IBeneficialOwnershipPdfTreeGroup[] = [];
  let rowCount = 0;
  let truncated = false;

  const append = (
    rows: IBeneficialOwnershipPdfTreeRow[],
    parentKey: string,
    edge: IOwnershipEdge,
    depth: number,
    path: string[],
  ): void => {
    if (rowCount >= maxRows) {
      truncated = true;
      return;
    }

    const nodeKey = edge.to;
    const children = graph.edges[nodeKey] || [];
    const isCycle = path.indexOf(nodeKey) !== -1;
    const isReference = !isCycle && Boolean(expandedNodes[nodeKey]);
    const atDepthLimit = !isCycle && !isReference && depth >= maxDepth;
    const status: BeneficialOwnershipPdfTreeRowStatus = isCycle
      ? 'cycle'
      : isReference
      ? 'reference'
      : atDepthLimit && children.length
      ? 'depth-limit'
      : 'normal';

    rows.push({
      key: `${parentKey}>${nodeKey}:${edge.relation}:${rowCount}`,
      nodeKey: nodeKey,
      parentKey: parentKey,
      node: graph.nodes[nodeKey] || fallbackNode(nodeKey),
      edge: edge,
      depth: depth,
      childCount: children.length,
      status: status,
    });
    rowCount += 1;

    if (status !== 'normal' || !children.length) {
      return;
    }

    expandedNodes[nodeKey] = true;
    const nextPath = path.concat([nodeKey]);
    children.forEach((child) => {
      append(rows, nodeKey, child, depth + 1, nextPath);
    });
  };

  // One root's connections as band groups: the subject, or one jurisdiction
  // branch (whose path includes the subject, so a link back to it is a cycle).
  const appendBands = (
    rootKey: string,
    path: string[],
    branch?: IBeneficialOwnershipPdfTreeGroup['branch'],
  ): void => {
    const before = groups.length;
    BENEFICIAL_OWNERSHIP_PDF_BANDS.forEach((band) => {
      const roots = (graph.edges[rootKey] || []).filter(
        (edge) => edge.band === band.key,
      );
      if (!roots.length || rowCount >= maxRows) {
        if (roots.length && rowCount >= maxRows) {
          truncated = true;
        }
        return;
      }

      const rows: IBeneficialOwnershipPdfTreeRow[] = [];
      roots.forEach((edge) => {
        append(rows, rootKey, edge, 1, path);
      });
      if (rows.length) {
        groups.push({
          band: band.key,
          label: band.label,
          rootCount: roots.length,
          rows: rows,
          branch: branch,
        });
      }
    });
    if (!branch) {
      return;
    }
    // A branch with nothing under it still gets its company card.
    if (groups.length === before) {
      groups.push({
        band: 'other',
        label: '',
        rootCount: 0,
        rows: [],
        branch: branch,
      });
    }
    groups[before].branchStart = true;
  };

  const branches = graph.branches || [];
  if (!branches.length) {
    appendBands(graph.subject, [graph.subject]);
  }
  branches.forEach((key) => {
    // Each country's company is expanded in full, even where it shares people
    // with another branch.
    expandedNodes = emptyDictionary<boolean>();
    appendBands(key, [graph.subject, key], {
      key: key,
      node: graph.nodes[key] || fallbackNode(key),
      connectionCount: (graph.edges[key] || []).length,
    });
  });

  return { groups: groups, rowCount: rowCount, truncated: truncated };
};
